/**
 * The fee-uncertainty marker on a price: the small amber badge a price wears
 * when its exact fee schedule is unavailable, unsupported here, or estimated.
 *
 * #602 replaced the badge's native `title` with the Dashboard's click-to-open
 * help popover — the same treatment as the ⓘ beside Open Positions / Open
 * Orders (`HelpPopover`). A native tooltip was invisible on touch, unreachable
 * from the keyboard, and looked nothing like the help affordance traders
 * already know. The badge itself is unchanged: same glyph, same colours, same
 * 14px footprint, so no row grew and no price moved.
 *
 * `FEE_DISPLAY_MESSAGE` stays exported and unchanged — the ladder and table
 * cells around these prices fold it into their own multi-purpose `title`
 * strings, and those are a different disclosure with a different owner.
 */
import { cn } from '@/lib/infra/utils';
import type { FeeDisplayStatus } from '@/services/hydraFees';
import { HelpPopover } from './HelpPopover';

export const FEE_DISPLAY_MESSAGE: Record<FeeDisplayStatus, string | null> = {
  'not-applicable': null,
  loading: 'Fee schedule is still loading; showing the raw venue price.',
  missing: 'Fee schedule unavailable; showing the raw venue price.',
  unsupported: 'This fee schedule cannot be adjusted on this surface; showing the raw venue price.',
  estimated: 'Fee schedule is temporarily estimated conservatively.',
  exact: null,
};

/** Popover heading per state — names the condition in three or four words. */
const FEE_DISPLAY_HEADING: Record<FeeDisplayStatus, string | null> = {
  'not-applicable': null,
  // Null on purpose, and load-bearing: loading is transient and already renders
  // the raw price, so a marker here would flash a warning into every row during
  // ordinary startup. A null heading is what keeps the state unmarked.
  loading: null,
  missing: 'Fee schedule unavailable',
  unsupported: 'Fee not adjustable here',
  estimated: 'Fee estimated',
  exact: null,
};

/**
 * What the state means for the trader, below the message. Kept beside the
 * message so the two lines can never drift into contradicting each other.
 * Every line must stay truthful about which number is on screen: a raw price
 * is never described as fee-adjusted, and an estimate is never described as
 * exact.
 */
const FEE_DISPLAY_DETAIL: Record<FeeDisplayStatus, string | null> = {
  'not-applicable': null,
  loading: null,
  missing:
    'The price and odds shown have no fee subtracted, so they read better than they will fill. Your true cost is higher by whatever the venue charges.',
  unsupported:
    'The price and odds shown have no fee subtracted, so they read better than they will fill. Your true cost is higher by whatever the venue charges.',
  estimated:
    'The adjustment applied errs on the high side: your actual fee should land at or below what you see here, never above it.',
  exact: null,
};

/** Missing/unsupported states are the only ones the opt-in hide control suppresses. */
export function feeDisplayCanHide(status: FeeDisplayStatus): boolean {
  return status === 'missing' || status === 'unsupported';
}

export function FeeScheduleMarker({
  status,
  className,
}: {
  status: FeeDisplayStatus;
  className?: string;
}) {
  const message = FEE_DISPLAY_MESSAGE[status];
  const heading = FEE_DISPLAY_HEADING[status];
  // `loading` keeps its message — the surrounding cells' own titles still use
  // it — but has no heading, and that is what leaves it unmarked.
  if (!message || !heading) return null;

  const estimated = status === 'estimated';
  return (
    <HelpPopover
      testId="fee-schedule-marker"
      label={`${heading} — show explanation`}
      title={heading}
      // `shrink-0` belongs on the WRAPPER now that the badge has one: these
      // markers sit in tight `inline-flex` price rows, and a shrinkable wrapper
      // would squeeze the badge into an ellipse on a narrow column.
      className={cn('shrink-0', className)}
      // Short enough to be worth hearing on every Tab, and the one audience
      // the old native `title` served worst.
      srDescription={`${heading}. ${message} ${FEE_DISPLAY_DETAIL[status]}`}
      panelWidth={400}
      triggerClassName={[
        'inline-flex h-3.5 w-3.5 shrink-0 cursor-pointer items-center justify-center rounded-full border text-[9px] font-bold leading-none',
        'focus-visible:ring-1 focus-visible:ring-amber-400 focus:outline-none',
        estimated
          ? 'border-amber-500/70 bg-amber-500/10 text-amber-400'
          : 'border-amber-500 bg-amber-500/20 text-amber-300',
      ].join(' ')}
      trigger={estimated ? '~' : '!'}
    >
      <p>{message}</p>
      <p className="mt-2">{FEE_DISPLAY_DETAIL[status]}</p>
    </HelpPopover>
  );
}
